// \src\components\feature\Footer.tsx

import { Link } from "react-router-dom";
import { useAdmin } from "@/context/AdminContext";

const currentYear = new Date().getFullYear();

const footerLinks = {
  empresa: [
    { label: "A Empresa", path: "/empresa" },
    { label: "Vendedores", path: "/vendedores" },
    { label: "MZ Outlet", path: "/outlet" },
    { label: "Notícias", path: "/noticias" },
  ],
  produtos: [
    { label: "Todos os Produtos", path: "/produtos" },
    { label: "Promoções", path: "/promocoes" },
    { label: "Categorias", path: "/produtos" },
  ],
  contato: [
    { label: "Contato", path: "/contato" },
    { label: "Trabalhe Conosco", path: "/trabalhe-conosco" },
  ],
};

export default function Footer() {
  const { data } = useAdmin();
  const founded = data.companyInfo.foundedYear || 2011;
  const s = data.settings;

  const socialLinks = [
    { icon: "ri-facebook-fill", label: "Facebook", url: s.socialFacebook, color: "hover:text-primary-400" },
    { icon: "ri-instagram-line", label: "Instagram", url: s.socialInstagram, color: "hover:text-primary-400" },
    { icon: "ri-whatsapp-line", label: "WhatsApp", url: s.socialWhatsapp, color: "hover:text-primary-400" },
    { icon: "ri-youtube-line", label: "YouTube", url: s.socialYoutube, color: "hover:text-primary-400" },
  ];

  return (
    <footer className="bg-foreground-950 text-background-200">
      {/* Top CTA Strip */}
      <div className="bg-primary-500 w-full py-5 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-background-50 font-heading text-xl font-semibold">
              Precisa de materiais para sua obra?
            </p>
            <p className="text-background-100 text-sm">
              Fale com um de nossos especialistas agora mesmo
            </p>
          </div>

          <a
            href={s.socialWhatsapp}
            target="_blank"
            rel="nofollow noreferrer"
            className="flex items-center gap-2 bg-background-50 text-primary-600 hover:bg-background-100 px-6 py-3 rounded-md font-label font-semibold text-sm transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-whatsapp-line text-lg" />
            Chamar no WhatsApp
          </a>
        </div>
      </div>

      {/* Main Footer */}
      <div className="w-full py-12 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">

          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-4 cursor-pointer">
              <img
                src="/images/logo/icon.png"
                alt={data.settings.siteName}
                className="h-16 w-auto object-contain"
              />
            </Link>

            <p className="text-background-400 text-sm leading-relaxed mb-4 max-w-xs">
              Desde {founded}, fornecendo os melhores materiais de construção com qualidade, economia e o melhor atendimento do mercado.
            </p>

            <div className="flex items-center gap-3">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.url}
                  target="_blank"
                  rel="nofollow noreferrer"
                  aria-label={s.label}
                  className={`w-9 h-9 flex items-center justify-center rounded-full border border-background-700 text-background-400 transition-all cursor-pointer hover:border-primary-500 hover:bg-primary-500 hover:text-background-50 ${s.color}`}
                >
                  <i className={`${s.icon} text-base`} />
                </a>
              ))}
            </div>
          </div>

          {/* Empresa */}
          <div>
            <h4 className="text-background-50 font-heading font-semibold text-sm uppercase tracking-widest mb-4">
              <a id="footer-empresa" href="/empresa">Empresa</a>
            </h4>

            <ul className="space-y-2">
              {footerLinks.empresa.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-background-400 hover:text-primary-400 text-sm transition-colors cursor-pointer"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Produtos */}
          <div>
            <h4 className="text-background-50 font-heading font-semibold text-sm uppercase tracking-widest mb-4">
              <a id="footer-produtos" href="/produtos">Produtos</a>
            </h4>

            <ul className="space-y-2">
              {footerLinks.produtos.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-background-400 hover:text-primary-400 text-sm transition-colors cursor-pointer"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contato */}
          <div>
            <h4 className="text-background-50 font-heading font-semibold text-sm uppercase tracking-widest mb-4">
              <a id="footer-contato" href="/contato">Contato</a>
            </h4>

            <ul className="space-y-2 mb-4">
              {footerLinks.contato.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-background-400 hover:text-primary-400 text-sm transition-colors cursor-pointer"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="space-y-1">
              <p className="text-background-500 text-xs uppercase tracking-wide mb-2">Nossas Lojas</p>
              <p className="text-background-400 text-xs">Loja Central: {s.contactPhoneCentral}</p>
              <p className="text-background-400 text-xs">MZ Outlet: {s.contactPhoneFilial}</p>
              <a href={`mailto:${s.contactEmail}`} className="block text-background-400 hover:text-primary-400 text-xs transition-colors cursor-pointer break-all">{s.contactEmail}</a>
              <p className="text-background-400 text-xs">Seg–Sex: {s.contactHoursWeek}</p>
              <p className="text-background-400 text-xs">Sab: {s.contactHoursSat}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-foreground-800 w-full py-4 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-background-600">
          <p>
            &copy; {founded}–{currentYear} {s.siteName}. Todos os direitos reservados.
          </p>

          <p>
            Desenvolvido por{" "}
            <a
              href="https://www.SistemaGenial.com.br"
              target="_blank"
              rel="nofollow noreferrer"
              className="text-primary-400 hover:text-primary-300 cursor-pointer"
            >
              SistemaGenial.com
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}